import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { parseReminderDays, reminderText } from "./notify";
import { SkeletonSection } from "./Skeleton";

/** Rule I-4: the days that an issue stays open before its assignee gets a reminder mail. */
export function IssueReminderSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.IssueReminder | "failed" | undefined>(undefined);
  const [days, setDays] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.IssueReminder) => {
    setFound(next);
    setDays(String(next.days));
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.issueReminder)
      .then(show, () => setFound("failed"));
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Issue reminders" rows={4} />;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Issue reminders</legend>
        <p className="error">Cannot load the issue reminder. Try again later.</p>
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const value = parseReminderDays(days, found.max);
    if (typeof value === "string") {
      setMessage(null);
      setProblem(value);
      input.current?.focus();
      return;
    }
    setProblem(null);
    setSaving(true);
    setMessage(null);
    try {
      const next = await api.setIssueReminder(await user.getIdToken(), value);
      show(next);
      setMessage({ ok: true, text: `Saved. ${reminderText(next.days)}` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The issue reminder was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="issue-reminder">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Issue reminders</legend>
        <div className="limit">
          <label htmlFor="issue-reminder-days">Days before the reminder</label>
          <input
            id="issue-reminder-days"
            data-testid="issue-reminder-days"
            ref={input}
            inputMode="numeric"
            value={days}
            aria-invalid={problem !== null || undefined}
            aria-describedby={problem ? "issue-reminder-error issue-reminder-hint" : "issue-reminder-hint"}
            onChange={(e) => {
              setDays(e.target.value);
              setMessage(null);
              setProblem(null);
            }}
          />
          {problem && (
            <p id="issue-reminder-error" role="alert" className="error" data-testid="issue-reminder-error">
              {problem}
            </p>
          )}
          <p id="issue-reminder-hint" className="hint">
            {reminderText(found.days)} An issue with an assignee who is not active goes to the operations managers, or to the administrators when there is no operations manager. 0 turns the mail off. Default: {found.default}.
          </p>
        </div>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-issue-reminder">
              {saving ? "Saving…" : "Save the reminder"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="issue-reminder-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
